/**
 * Custom hook for handling reCAPTCHA submission with comprehensive error handling
 */
import { useCallback } from 'react';
import { executeRecaptcha as executeRecaptchaUtil, getRecaptchaSiteKey } from '@/lib/utils/recaptcha';
import { log } from '@/lib/utils/logger';
import { RECAPTCHA_CONSTANTS, MESSAGE_CONSTANTS } from '@/lib/constants/appConstants';
import { 
  RecaptchaError, 
  RecaptchaTimeoutError, 
  NetworkTimeoutError, 
  NetworkError, 
  FetchError,
  SlowNetworkError
} from '@/lib/errors/AppErrors';

interface BadgeControls {
  showWithLoading: () => void;
  showReady: () => void;
  hide: () => void;
}

interface UseRecaptchaSubmitProps {
  networkSlow: boolean;
  badgeControls: BadgeControls;
  minTokenLength?: number; // Minimum acceptable token length (default: from constants)
  maxRetryDuration?: number; // Maximum time to spend retrying for short tokens (default: from constants)
}

export const useRecaptchaSubmit = ({ 
  networkSlow, 
  badgeControls, 
  minTokenLength = RECAPTCHA_CONSTANTS.VALIDATION.MIN_TOKEN_LENGTH, 
  maxRetryDuration = RECAPTCHA_CONSTANTS.VALIDATION.MAX_RETRY_DURATION 
}: UseRecaptchaSubmitProps) => {
  const executeRecaptcha = useCallback(async (): Promise<string> => {
    // Get reCAPTCHA token with enhanced error handling for slow networks
    const siteKey = await getRecaptchaSiteKey();
    if (!siteKey) {
      throw new RecaptchaError('reCAPTCHA site key not configured');
    }
    
    // Show loading state on badge
    badgeControls.showWithLoading();
    
    try {
      log.debug('Executing reCAPTCHA...');
      
      // Use the robust reCAPTCHA utility that throws proper error types with network awareness
      const captchaToken = await executeRecaptchaUtil(
        siteKey, 
        RECAPTCHA_CONSTANTS.ACTIONS.LOGIN,
        undefined, // Let the function auto-select timeout
        networkSlow,
        minTokenLength, // Minimum token length validation
        maxRetryDuration // Maximum time to spend retrying for short tokens
      );
      
      log.debug(`reCAPTCHA token obtained successfully (length: ${captchaToken.length})`);
      return captchaToken;
    } catch (error) {
      log.error('reCAPTCHA execution failed:', error);
      throw error; // Re-throw with proper error types
    } finally {
      // Hide badge after execution
      badgeControls.hide();
    }
  }, [networkSlow, badgeControls]);

  // Helper function to determine error message based on error type
  const determineErrorMessage = useCallback((error: Error): string => {
    // Use type-safe error checking instead of brittle string matching
    if (error instanceof RecaptchaTimeoutError) {
      return MESSAGE_CONSTANTS.ERRORS.RECAPTCHA_TIMEOUT;
    } else if (error instanceof RecaptchaError) {
      // Check if it's a token length error
      if (error.message.includes('token too short')) {
        return MESSAGE_CONSTANTS.ERRORS.RECAPTCHA_TOKEN_SHORT;
      }
      return networkSlow 
        ? MESSAGE_CONSTANTS.ERRORS.RECAPTCHA_SLOW_NETWORK
        : MESSAGE_CONSTANTS.ERRORS.RECAPTCHA_FAILED;
    } else if (error instanceof SlowNetworkError) {
      return MESSAGE_CONSTANTS.ERRORS.RECAPTCHA_SLOW_NETWORK;
    } else if (error instanceof NetworkTimeoutError) {
      return MESSAGE_CONSTANTS.ERRORS.NETWORK_TIMEOUT;
    } else if (error instanceof FetchError || error instanceof NetworkError) {
      return MESSAGE_CONSTANTS.ERRORS.NETWORK_TIMEOUT;
    } else {
      // For unknown errors, use the error message if available
      return error.message || MESSAGE_CONSTANTS.ERRORS.API_ERROR;
    }
  }, [networkSlow]);

  const showErrorMessage = useCallback((error: Error): string => {
    const errorMessage = determineErrorMessage(error);
    log.error('reCAPTCHA submission error:', errorMessage);
    return errorMessage;
  }, [determineErrorMessage]);

  return {
    executeRecaptcha,
    showErrorMessage
  };
};
